import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, QueryList, Type, ViewChildren } from '@angular/core';
import { BaseComponent } from '@lib/components';
import { TableFilterComponent, TColumn, TColumnSort } from '@lib/modules/table/components';
import { ApiResponseError, IAuthResponse, ICustomer, IListResponse, ISearchTransactionResponse, ITransactionFilter, ITransactionIndex, Nullable } from '@lib/interfaces';
import { FilterMatcher, FilterOperator, Permission } from '@lib/enums';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountsService, ITransactionFilterArray, SidePanelService, TransactionSearchService, TransactionService } from '@lib/services';
import { SUPPRESS_EVENT } from '@lib/constants';
import { ISearchTransactionRequestForm } from '@lib/services';
import { ITableTemplate } from '@lib/modules/table/interfaces';
import { finalize } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { CustomersService } from '@lib/services/customer/customer.service';
import { tenantSettings } from 'src/theme';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';

@Component({
    template: '',
})
export class SharedSendMoneyComponent extends BaseComponent implements OnInit, AfterViewInit {
    @Input() public filters: Array<ITransactionFilter> = [];
    @Input() public isOpenedFromCardOverview = true;

    protected columns: Array<TColumn> = [
        { transaction_timestamp: 'Date Transferred' },
        { transaction_type: 'Type' },
        { sender: 'Sender' },
        { receiver_name: 'Receiver Name' },
        { amount_sent: 'Amount Sent' },
        { amount_received: 'Amount Received' },
        { transfer_status: 'Transfer Status' },
    ];

    protected defaultColumns: Array<string> = ['transaction_timestamp', 'transaction_type', 'sender', 'receiver_name', 'amount_sent', 'amount_received', 'transfer_status'];

    protected sortableColumns: Array<string> = [];
    protected transactions: Array<ITransactionIndex> = [];
    protected hasMoreRecords = true;
    protected loading = false;
    protected Permission: typeof Permission = Permission;
    protected isFilterApplied = false;
    protected userInfo: IAuthResponse['admin'] = JSON.parse(localStorage.getItem('userInfo') || '');
    protected apiInProgress = false;
    protected searchPayload: FormGroup<ISearchTransactionRequestForm> = this.createSearchTransactionFormGroup();
    protected redirectToComponent: Type<any>;
    private fromState: string | null = null;

    @ViewChildren(TableFilterComponent) filterControls: QueryList<ITableTemplate>;

    public constructor(
        protected readonly formBuilder: FormBuilder,
        protected readonly matSnackBar: MatSnackBar,
        protected readonly transactionService: TransactionService,
        protected readonly transactionSearchService: TransactionSearchService,
        protected readonly customerService: CustomersService,
        protected readonly accountService: AccountsService,
        protected sidePanel: SidePanelService,
        protected cdr: ChangeDetectorRef,
        protected dialog: MatDialog,
        private router: Router,
    ) {
        super();
    }

    public ngOnInit(): void {
        // this.observeSearchPayloadChanges();
        let fromState = null;

        // Works on HARD reload / refresh
        if (history.state?.from && history.state?.navigationId != 1) {
            fromState = history.state?.from || null;
        }
        if (fromState === 'home') {
            this.openSendMoneyModal();
        }

        this.reloadTransactionListner();
    }
    reloadTransactionListner() {
        this.addSubscription(
            this.sidePanel.dialog.afterAllClosed.subscribe(() => {
                this.transactions = [];
                this.searchPayload = this.createSearchTransactionFormGroup();
                this.applyBuiltInFilters();
                this.reFetchTransactions();
            }),
        );
    }

    ngAfterViewInit() {
        this.cdr.detectChanges();
    }

    protected searchTransactions(): void {
        if (this.apiInProgress) return;

        this.apiInProgress = true;

        this.addSubscription(
            this.transactionService
                .searchTransactions(this.searchPayload.value)
                .pipe(
                    finalize((): void => {
                        this.loading = false;
                        this.apiInProgress = false;
                    }),
                )
                .subscribe({
                    next: ({ data, paging }: ISearchTransactionResponse): void => {
                        this.transactions = [...this.transactions, ...data];
                        this.cdr.detectChanges();

                        this.searchPayload.removeControl('cursors', SUPPRESS_EVENT);

                        // paging.cursors.has_more can be explicitly false while `paging` itself is
                        // still present — relying on `paging`'s mere presence alone kept re-fetching
                        // and re-appending the same last page forever on the Wallet Transfer tab.
                        if (!paging || paging.cursors.has_more === false) {
                            this.hasMoreRecords = false;

                            return;
                        }

                        this.searchPayload.addControl(
                            'cursors',
                            this.formBuilder.group({
                                after: this.formBuilder.nonNullable.control(paging.cursors.after),
                            }),
                            SUPPRESS_EVENT,
                        );
                    },
                    error: (error: ApiResponseError): void => {
                        this.hasMoreRecords = false;

                        this.createApiErrorSnackBar(this.matSnackBar, error);
                    },
                }),
        );
    }

    protected applySort(sortBy: Nullable<TColumnSort>): void {
        this.clearTransactions();

        this.searchPayload.removeControl('sort_by', SUPPRESS_EVENT);
        this.searchPayload.removeControl('order_by', !sortBy ? {} : SUPPRESS_EVENT);

        if (!sortBy) return;

        this.searchPayload.addControl('sort_by', this.formBuilder.nonNullable.control(sortBy.key), SUPPRESS_EVENT);
        this.searchPayload.addControl('order_by', this.formBuilder.nonNullable.control(sortBy.sort));
    }

    private observeSearchPayloadChanges(): void {
        this.addSubscription(
            this.searchPayload.valueChanges.subscribe((): void => {
                this.reFetchTransactions();
            }),
        );
    }

    protected reFetchTransactions(): void {
        this.loading = true;
        this.hasMoreRecords = true;

        this.searchTransactions();
    }

    private clearTransactions(): void {
        this.searchPayload.removeControl('cursors', SUPPRESS_EVENT);

        this.transactions = [];
    }

    protected createSearchTransactionFormGroup(): FormGroup<ISearchTransactionRequestForm> {
        return this.formBuilder.group<ISearchTransactionRequestForm>({
            filters: this.formBuilder.array<ITransactionFilterArray>([]),
            limit: this.formBuilder.nonNullable.control<number>(10),
            matcher: this.formBuilder.nonNullable.control<FilterMatcher>(FilterMatcher.AND),
        });
    }

    protected applyBuiltInFilters(): void {
        this.filters = [
            {
                field: 'user_id',
                operator: FilterOperator.ALL_OF,
                values: [this.userInfo.id],
            },
            {
                field: 'transaction_type',
                operator: FilterOperator.ALL_OF,
                values: ['WALLET_TRANSFER'],
            },
        ];
        for (const { field, operator, values } of this.filters) {
            this.transactionSearchService.addFilter(this.searchPayload, field, operator, values, false);
        }
    }

    removeFilters() {
        this.isFilterApplied = false;
        this.transactionSearchService.removeAllFilters(this.searchPayload);

        this.loading = true;

        this.clearTransactions();
        this.reFetchTransactions();
    }
    openSendMoneyModal(): void {
        const username = localStorage.getItem('username');
        if (!username) {
            const error = new Error('No User Found!!');
            this.createApiErrorSnackBar(this.matSnackBar, error);
            this.loading = false;
            return;
        }
        this.loading = true; // Set loading to true at the start of the operation
        const currentTenant = localStorage.getItem('tenantKey') || '';
        const neoTenant = tenantSettings.find((tenant) => tenant.name.toLowerCase() === 'neo');
        if (currentTenant === neoTenant?.key) {
            this.customerService.fetchAgentDetailsByMobile(username).subscribe({
                next: ({ data }: IListResponse<ICustomer>) => {
                    if (data.length === 0) {
                        const error = new Error('No accounts found!!');
                        this.createApiErrorSnackBar(this.matSnackBar, error);
                        this.loading = false;
                        return;
                    }
                    this.getSenderAccountDetails(data[0].id), currentTenant === neoTenant?.key;
                },
                error: (error: ApiResponseError) => {
                    this.loading = false;
                    this.createApiErrorSnackBar(this.matSnackBar, error);
                },
            });
        } else {
            const isSubAgent = localStorage.getItem('isSubAgent');
            const parentUserId = localStorage.getItem('parentUserId')!;
            this.getSenderAccountDetails(isSubAgent ? parentUserId : this.userInfo.id, currentTenant === neoTenant?.key);
        }
    }

    private getSenderAccountDetails(id: string, isNeoTenant = false) {
        this.accountService.getAccountsByUserId(id).subscribe({
            next: ({ data: accounts }) => {
                const senderAccount = accounts?.filter((account) => {
                    return account.type === 'MASTER';
                });
                if (senderAccount.length === 0 && isNeoTenant) {
                    const error = new Error('No Master Wallet found!!');
                    this.createApiErrorSnackBar(this.matSnackBar, error);
                    this.loading = false;
                    return;
                }
                this.sidePanel.open(this.redirectToComponent, { data: { account: senderAccount } });
                this.loading = false;
            },
            error: (error: ApiResponseError) => {
                this.loading = false;
                this.createApiErrorSnackBar(this.matSnackBar, error);
            },
        });
    }
}
